import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash.debounce';

import Breakpoints from 'lib/Breakpoints';

export const UPDATE_CURRENT_PAGE_TIMEOUT = 500;
export const ON_SCROLL_DEBOUNCE_TIMEOUT = 250;

const THRESHOLD_FOR_NEW_PAGE = 0.25;

/**
 * Calculates pagination data for a slider component.
 *
 * @param {HTMLElement} container - The container element holding the cards.
 * @param {string} cardWrapperClass - The class name for card wrapper elements.
 * @param {number} fallbackCardWidthValue - Fallback width to use if card width cannot be determined.
 * @param {number} paginationButtonSize - The size (width) of the pagination button(s) to subtract from container width.
 * @returns {{ cardWidth: number; containerWidth: number; totalPages: number; pageWidt: number }|null} An object containing cardWidth, containerWidth, totalPages, and pageWidth, or null if no container.
 */
export const calculatePaginationData = (
  container,
  cardWrapperClass,
  fallbackCardWidthValue,
  paginationButtonSize,
) => {
  if (!container) {
    return null;
  }

  const containerWidth = container.clientWidth
    ? Math.max(container.clientWidth - paginationButtonSize, 1)
    : 1;

  const cards = container.querySelectorAll(`.${cardWrapperClass}`);
  const [card] = cards;
  const cardWidth = card?.offsetWidth || fallbackCardWidthValue || 1;
  const numCardVisibleInContainer = Math.floor(
    containerWidth / cardWidth,
  ) || 1;

  const pageWidth = numCardVisibleInContainer * cardWidth;
  const scrollWidth = container.scrollWidth || (cards.length * cardWidth);
  const calculatedPages = Math.ceil((scrollWidth / pageWidth) - THRESHOLD_FOR_NEW_PAGE);
  const pages = Math.min(cards.length, calculatedPages);

  return {
    cardWidth,
    containerWidth,
    totalPages: pages || 1,
    pageWidth,
  };
};

/**
 * Calculates the current page index based on the scroll position and container dimensions.
 *
 * @param {object} params - The parameters for calculation.
 * @param {number} params.containerScrollLeft - The current horizontal scroll position of the container.
 * @param {number} params.containerScrollWidth - The total scrollable width of the container.
 * @param {number} params.containerWidth - The visible width of the container.
 * @param {number} params.pageWidth - The width of a single page.
 * @param {number} params.totalPages - The total number of pages.
 * @returns {number} The current page index (1-based).
 */
export const calculateCurrentPage = ({
  containerScrollLeft,
  containerScrollWidth,
  containerWidth,
  pageWidth,
  totalPages,
}) => {
  if (!containerScrollLeft || !containerWidth) {
    return 1;
  }

  if (containerScrollLeft + containerWidth >= containerScrollWidth) {
    return totalPages;
  }

  return Math.floor((containerScrollLeft / pageWidth) + 1.5);
};

/**
 * @typedef {object} UseSliderPaginationReturn
 * @property {number} currentPage - The current page number.
 * @property {boolean} shouldShowPagination - Whether pagination should be shown.
 * @property {number} totalPages - Total number of pages.
 * @property {Function} handleScrollPrev - Handler to scroll to the previous page.
 * @property {Function} handleScrollNext - Handler to scroll to the next page.
 * @property {Function} handleScrollToPage - Handler to scroll to a specific page.
 * @property {Function} onContainerScroll - Handler for container scroll events.
 */

/**
 * Custom React hook for managing slider pagination logic.
 *
 * @param {object} options - Configuration options.
 * @param {React.RefObject} options.scrollContainerRef - Ref to the scrollable container element.
 * @param {number} [options.paginationButtonSize=0] - Size of the pagination button (in pixels).
 * @param {Function} [options.onScroll] - Optional callback for scroll events.
 * @param {string} [options.cardWrapperClass] - Class name for card wrapper elements.
 * @param {number | (isMobile: boolean) => number} [options.fallbackCardWidth=0] - Fallback width for cards if not calculated.
 * @returns {UseSliderPaginationReturn} Pagination state and handlers.
 */
export const useSliderPagination = (
  {
    scrollContainerRef,
    paginationButtonSize = 0,
    onScroll,
    cardWrapperClass,
    fallbackCardWidth,
  },
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [shouldShowPagination, setShouldShowPagination] = useState(true);
  const [pageWidth, setPageWidth] = useState(0);
  const isScrollingRef = useRef(false);

  const setPaginationData = useCallback((isMobile) => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    const fallbackCardWidthValue = typeof fallbackCardWidth === 'function'
      ? fallbackCardWidth(isMobile)
      : fallbackCardWidth;

    const paginationData = calculatePaginationData(
      container,
      cardWrapperClass,
      fallbackCardWidthValue,
      paginationButtonSize,
    );

    if (!paginationData) {
      return;
    }

    setTotalPages(paginationData.totalPages);
    setPageWidth(paginationData.pageWidth);

    // NOTE: Pagination is disabled on mobile
    setShouldShowPagination(isMobile ? false : paginationData.totalPages > 1);
  }, [scrollContainerRef, cardWrapperClass, paginationButtonSize, fallbackCardWidth]);

  const onBreakpointChange = useCallback(() => {
    setPaginationData(Breakpoints.isS());
  }, []);

  useEffect(() => {
    onBreakpointChange();
    window.addEventListener('resize', onBreakpointChange);

    return () => {
      window.removeEventListener('resize', onBreakpointChange);
    };
  }, [setPaginationData]);

  const handleScrollTo = useCallback((page) => {
    const pageOffset = pageWidth * (page - 1);

    if (isScrollingRef.current) {
      return;
    }

    isScrollingRef.current = true;

    scrollContainerRef.current.scrollTo({
      behavior: 'smooth',
      left: pageOffset,
    });

    setTimeout(() => {
      isScrollingRef.current = false;
      setCurrentPage(page);
    }, UPDATE_CURRENT_PAGE_TIMEOUT);
  }, [pageWidth, scrollContainerRef]);

  const handleScrollPrev = useCallback(() => {
    if (currentPage > 1) {
      handleScrollTo(currentPage - 1);
    }
  }, [handleScrollTo, currentPage]);

  const handleScrollNext = useCallback(() => {
    const shouldLoopToStart = currentPage === totalPages;
    handleScrollTo(shouldLoopToStart ? 1 : currentPage + 1);
  }, [handleScrollTo, totalPages, currentPage]);

  const handleScrollToPage = useCallback((page) => {
    handleScrollTo(page);
  }, [handleScrollTo]);

  const onContainerScroll = useCallback(debounce((e) => {
    const container = scrollContainerRef.current;

    if (!container || isScrollingRef.current) {
      return;
    }

    const calculatedPage = calculateCurrentPage({
      containerScrollLeft: container.scrollLeft,
      containerScrollWidth: container.scrollWidth,
      containerWidth: container.clientWidth,
      pageWidth,
      totalPages,
    });

    setCurrentPage(calculatedPage);
    onScroll?.(e, {
      x: scrollContainerRef.current.scrollLeft + scrollContainerRef.current.clientWidth,
      width: scrollContainerRef.current.scrollWidth,
    });
  }, ON_SCROLL_DEBOUNCE_TIMEOUT), [
    scrollContainerRef,
    pageWidth,
    paginationButtonSize,
    totalPages,
    onScroll,
  ]);

  return {
    currentPage,
    shouldShowPagination,
    totalPages,
    handleScrollPrev,
    handleScrollNext,
    handleScrollToPage,
    onContainerScroll,
  };
};
